import { PropsWithChildren, useRef } from "react";
import { useResizeObserver } from "usehooks-ts";
import { cn } from "web-design-system";

type ScaledFrameProps = PropsWithChildren<{
  /** Width the content is designed at */
  width: number;
  /** Height the content is designed at */
  height: number;
  className?: string;
}>;

/**
 * Renders children at a fixed design size and scales them down to fit inside
 * the container (both width and height), so previews can be built with
 * regular sizes. The container needs a definite size.
 */
export function ScaledFrame({
  width,
  height,
  className,
  children,
}: ScaledFrameProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { width: availableWidth, height: availableHeight } = useResizeObserver({
    ref: ref as React.RefObject<HTMLDivElement>,
  });
  const scale =
    availableWidth && availableHeight
      ? Math.min(availableWidth / width, availableHeight / height, 1)
      : 0;

  return (
    <div ref={ref} className={cn("relative size-full", className)}>
      <div
        className={cn(
          "absolute top-0 left-1/2 origin-top",
          !scale && "invisible"
        )}
        style={{
          width,
          height,
          transform: `translateX(-50%) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}
